<template>
    <div v-if="loadingVersions" class="d-flex version-loader">
        <b-spinner class="loader-spinner mr-3" />
        <p class="text">Loading versions...</p>
    </div>
    <div v-else class="version-restore">
        <div class="info-message mb-3">
            <span class="info-icon">
                <font-awesome-icon :icon="['fa-solid', 'fa-circle-info']" />
            </span>
            <span class="info-text">
                Select a previous version of your {{ parseEntity().toLowerCase() }} to create a restored copy.
            </span>
        </div>
        <div class="mb-3">
            <div class="entity-name">{{ entityName }}</div>
            <div class="entity-id">{{ entityId }}</div>
        </div>

        <select v-model="version" class="custom-select" name="versions-select">
            <option value="" selected>-- Select version --</option>
            <option
                v-for="({ VersionId, LastModified }, index) in versions"
                :key="`dropdown-${index}`"
                :value="VersionId"
            >
                {{ LastModified.replace('T', ' ') }}
            </option>
        </select>

        <div v-show="errorMessage" class="error-message w-100 pb-3 text-center">
            {{ errorMessage }}
        </div>

        <div class="modal-footer row pb-0">
            <div class="btn-back col-xs-6 text-left">
                <b-button variant="outline-dark" :disabled="saving" @click="goBack">Back</b-button>
            </div>
            <div class="btn-restore col-xs-6 text-right">
                <loader-btn
                    variant="primary"
                    type="submit"
                    loading-text="Restoring"
                    :loading="saving"
                    :disabled="saving || version === ''"
                    @click="restoreEntityVersion"
                >
                    Restore
                </loader-btn>
            </div>
        </div>
    </div>
</template>
<script setup lang="ts">
import { AxiosError } from 'axios';
import { onMounted, ref } from 'vue';

import { AuditEntity } from '@extTypes/auditLog/types';
import { EntityVersionRestored, VersionInfo } from '@extTypes/versioning';
import { Settings } from '@extTypes/settings/settings';
import { OptionSetDto } from '@extTypes/options/optionSet';

import LoaderBtn from '@/components/LoaderBtn/LoaderBtn.vue';

const props = defineProps<{
    entityId?: string | number;
    entityType: AuditEntity;
    entityName: string;
    getVersion: (key: string, versionId: string) => Promise<Settings.Settings | OptionSetDto>;
    listVersions: (id: string | number) => Promise<VersionInfo[]>;
    restoreVersion: (data: Settings.Settings | OptionSetDto, id: string | number) => Promise<void>;
}>();

const emit = defineEmits<{
    (event: 'go-back'): void;
    (event: 'entity-version-restored', data: EntityVersionRestored): void;
}>();

const version = ref('');
const saving = ref(false);
const loadingVersions = ref(false);
const errorMessage = ref('');
const versions = ref<VersionInfo[]>([]);

const goBack = () => {
    emit('go-back');
};

const restoreEntityVersion = async () => {
    if (!props.entityId) {
        errorMessage.value = 'No entity selected for restoration.';
        return;
    }
    try {
        saving.value = true;
        errorMessage.value = '';
        const selected = versions.value.find((v) => v.VersionId === version.value);
        if (!selected) return;

        const data = await props.getVersion(selected.Key, selected.VersionId);
        await props.restoreVersion(data, props.entityId);
        emit('entity-version-restored', { entityId: props.entityId, data });
    } catch (error) {
        const axiosError = error as AxiosError;
        errorMessage.value =
            axiosError.response?.data?.Message ?? `Error restoring version for ${parseEntity()} ${props.entityId}`;
    } finally {
        saving.value = false;
    }
};

const getEntityVersions = async () => {
    if (!props.entityId) {
        versions.value = [];
        return;
    }
    try {
        loadingVersions.value = true;
        versions.value = await props.listVersions(props.entityId);
    } catch (error) {
        console.error(error);
        errorMessage.value = 'Error loading versions';
    } finally {
        loadingVersions.value = false;
    }
};

const parseEntity = (): string => {
    switch (props.entityType) {
        case AuditEntity.LIBRARY:
            return 'Library';
        case AuditEntity.OPTION_SET:
            return 'Option Set';
        case AuditEntity.PRODUCT:
            return 'Product';
        case AuditEntity.TEMPLATE:
            return 'Template';
        case AuditEntity.SETTING:
            return 'Setting';
        default:
            return 'Unknown';
    }
};

onMounted(getEntityVersions);
</script>
<style lang="scss" scoped>
.info-message {
    display: flex;
    align-items: center;
    gap: 10px;
    background-color: #f5f6f8;
    border-radius: 8px;
    border: 1px solid #e1e3e6;
    padding: 12px 16px;
    font-size: 14px;
}

.info-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    color: #333;

    i {
        font-size: 16px;
    }
}

.info-text {
    line-height: 1.4;
}

.version-loader {
    .text {
        font-size: 1.2em;
        margin: auto 0;
    }
}

.version-restore {
    .modal-footer {
        justify-content: space-between;
        margin-top: 25px;
        border-top: 1px solid #dee2e6;
        padding-left: 15px !important;
        padding-right: 15px !important;
    }

    .btn-back,
    .btn-restore {
        padding-left: 0 !important;
        padding-right: 0 !important;
        margin: 0 !important;
    }
}

.entity-name {
    color: var(--Gray-scale-gray-darkest, #333);
    font-size: 16px;
    font-weight: 700;
    margin-bottom: 5px;
}

.entity-id {
    color: var(--Primary-primary, #7258e0);
    font-size: 12px;
    font-weight: 400;
}
</style>
